import type { Trip, TripDestination } from '@/services/trips';

const destinationDateKey = (value: Date): number => {
  const normalized = new Date(value);
  normalized.setHours(0, 0, 0, 0);
  return normalized.getTime();
};

/** Stable key for comparing destination lists (avoids parent/child reset loops). */
export function tripDestinationsSnapshotKey(
  destinations: TripDestination[] | null | undefined,
): string {
  if (!destinations || destinations.length === 0) return '__empty__';

  return JSON.stringify(
    destinations.map((dest) => ({
      city: dest.city?.trim() ?? '',
      country: dest.country?.trim() ?? '',
      countryCode: dest.countryCode ?? '',
      start: destinationDateKey(dest.startDate),
      end: destinationDateKey(dest.endDate),
      startTime: dest.startTime ?? '',
      endTime: dest.endTime ?? '',
      locationId: dest.locationId ?? '',
      placeId: dest.placeId ?? '',
      lat:
        dest.coordinates?.latitude != null
          ? Math.round(dest.coordinates.latitude * 1e5) / 1e5
          : null,
      lng:
        dest.coordinates?.longitude != null
          ? Math.round(dest.coordinates.longitude * 1e5) / 1e5
          : null,
    })),
  );
}

// Returns the first city from destinations[], otherwise falls back to the legacy destination string.
export const getTripPrimaryDestination = (trip: Pick<Trip, 'destination' | 'destinations'>): string | undefined => {
  const firstDest = trip.destinations?.[0]?.city?.trim();
  if (firstDest) {
    return firstDest;
  }
  const legacy = trip.destination?.trim();
  return legacy || undefined;
};

/**
 * Destination string for landmark images: prefer structured `city + country` so slug extraction
 * matches curated maps (avoids noisy slugs like "toronto-canada" when city is stored as two words).
 */
export const getTripLandmarkDestination = (
  trip: Pick<Trip, 'destination' | 'destinations'>,
): string | undefined => {
  const row = trip.destinations?.[0];
  const city = row?.city?.trim();
  const country = row?.country?.trim();
  if (city && country) {
    return `${city}, ${country}`;
  }
  if (city) {
    return city;
  }
  return trip.destination?.trim() || undefined;
};

// Builds a concise display label for multi-city trips.
// Example: [Paris, Rome, Venice] => "Paris • Rome • Venice"
// If more than maxCities, show the remainder count: [Paris, Rome, Venice, Milan] => "Paris • Rome • Venice +1"
export const getTripDestinationLabel = (
  trip: Pick<Trip, 'destination' | 'destinations'>,
  options?: { maxCities?: number }
): string | undefined => {
  const maxCities = options?.maxCities ?? 3;

  const cities: string[] =
    trip.destinations
      ?.map((d: TripDestination) => d?.city?.trim())
      .filter(Boolean) as string[] || [];

  if (cities.length === 0) {
    return trip.destination?.trim() || undefined;
  }

  if (cities.length <= maxCities) {
    return cities.join(' • ');
  }

  const visible = cities.slice(0, maxCities);
  const remaining = cities.length - maxCities;
  return `${visible.join(' • ')} +${remaining}`;
};

// Produces a lowercased search text that includes name, legacy destination, and all multi-city destinations.
export const getTripDestinationSearchText = (
  trip: Pick<Trip, 'name' | 'destination' | 'destinations'>
): string => {
  const parts: string[] = [];
  if (trip.name) parts.push(trip.name);
  if (trip.destination) parts.push(trip.destination);
  if (trip.destinations?.length) {
    trip.destinations.forEach((d) => {
      if (d.city) parts.push(d.city);
      if (d.country) parts.push(d.country);
    });
  }
  return parts
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean)
    .join(' ');
};

